import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faPlay, 
  faPause,
  faScissors, 
  faArrowUp,
  faSpinner,
  faVolumeMute,
  faVolumeUp
} from '@fortawesome/free-solid-svg-icons';
import styles from './VideoTrimmerModal.module.css';

const VideoTrimmerModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  file, 
  maxDuration, 
  originalDuration 
}) => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const timelineRef = useRef(null);
  const videoUrl = useRef(null);
  const isMountedRef = useRef(true);
  
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [duration, setDuration] = useState(originalDuration || 0);
  const [currentTime, setCurrentTime] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [thumbnails, setThumbnails] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragType, setDragType] = useState(null);

  // Format seconds to MM:SS
  const formatTime = useCallback((seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Generate thumbnails for timeline
  const generateThumbnails = useCallback(async (video, count = 10) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      canvas.width = 80;
      canvas.height = 45;
      
      let completed = 0;
      const thumbnails = [];
      
      for (let i = 0; i < count; i++) {
        const time = (video.duration / count) * i;
        
        const generateThumb = () => {
          if (!isMountedRef.current) return;
          
          const onSeeked = () => {
            video.removeEventListener('seeked', onSeeked);
            try {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              thumbnails[i] = {
                time,
                dataUrl: canvas.toDataURL('image/jpeg', 0.7)
              };
            } catch (error) {
              console.warn('Error generating thumbnail:', error);
              thumbnails[i] = {
                time,
                dataUrl: ''
              };
            }
            
            completed++;
            if (completed === count && isMountedRef.current) {
              resolve(thumbnails.filter(t => t));
            }
          };
          
          video.addEventListener('seeked', onSeeked);
          video.currentTime = time;
        };
        
        setTimeout(generateThumb, i * 50);
      }
    });
  }, []);

  // Clean up function
  const cleanup = useCallback(() => {
    if (videoRef.current) {
      const video = videoRef.current;
      try {
        video.pause();
        video.currentTime = 0;
      } catch (error) {
        console.warn('Error cleaning up video:', error);
      }
    }
    
    if (videoUrl.current) {
      URL.revokeObjectURL(videoUrl.current);
      videoUrl.current = null;
    }
  }, []);

  // Initialize video and thumbnails
  useEffect(() => {
    isMountedRef.current = true;
    
    if (!isOpen || !file || !videoRef.current) return;

    // Cleanup previous instance
    cleanup();

    // Reset states
    setIsReady(false);
    setIsPlaying(false);
    setCurrentTime(0);
    setThumbnails([]);

    const video = videoRef.current;
    videoUrl.current = URL.createObjectURL(file);
    video.src = videoUrl.current;
    video.muted = isMuted;
    
    const handleLoadedMetadata = async () => {
      if (!isMountedRef.current) return;
      
      const videoDuration = video.duration;
      setDuration(videoDuration);
      setTrimStart(0);
      setTrimEnd(Math.min(videoDuration, maxDuration));
      setCurrentTime(0);
      setIsReady(true);
      
      try {
        // Generate thumbnails
        const thumbs = await generateThumbnails(video, 10);
        if (isMountedRef.current) {
          setThumbnails(thumbs);
        }
      } catch (error) {
        console.error('Error generating thumbnails:', error);
      }
    };

    const handleTimeUpdate = () => {
      if (!isDragging && isMountedRef.current) {
        const time = video.currentTime;
        setCurrentTime(time);
        
        // Auto-pause when reaching trim end during playback
        if (time >= trimEnd && isPlaying) {
          video.pause();
          setIsPlaying(false);
        }
      }
    };

    const handlePlay = () => isMountedRef.current && setIsPlaying(true);
    const handlePause = () => isMountedRef.current && setIsPlaying(false);
    const handleEnded = () => isMountedRef.current && setIsPlaying(false);

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    return () => {
      isMountedRef.current = false;
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      cleanup();
    };
  }, [isOpen, file, maxDuration, generateThumbnails, isDragging, trimEnd, isPlaying, isMuted, cleanup]);

  // Control functions
  const togglePlayPause = useCallback(() => {
    if (!videoRef.current || !isReady) return;
    
    const video = videoRef.current;
    
    try {
      if (isPlaying) {
        video.pause();
      } else {
        // Start playing from trim start if current time is outside trim range
        if (currentTime < trimStart || currentTime >= trimEnd) {
          video.currentTime = trimStart;
        }
        video.play();
      }
    } catch (error) {
      console.error('Error toggling playback:', error);
    }
  }, [isReady, isPlaying, currentTime, trimStart, trimEnd]);

  const playTrimmedSection = useCallback(() => {
    if (!videoRef.current || !isReady) return;
    
    try {
      const video = videoRef.current;
      video.currentTime = trimStart;
      video.play();
      setCurrentTime(trimStart);
    } catch (error) {
      console.error('Error playing trimmed section:', error);
    }
  }, [isReady, trimStart]);

  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      const newMutedState = !isMuted;
      videoRef.current.muted = newMutedState;
      setIsMuted(newMutedState);
    }
  }, [isMuted]);

  // Timeline interaction helpers
  const getTimelinePosition = useCallback((clientX) => {
    if (!timelineRef.current) return 0;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    return percent * duration;
  }, [duration]);

  const seekToTime = useCallback((time) => {
    if (!videoRef.current || !isReady) return;
    
    try {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    } catch (error) {
      console.error('Error seeking:', error);
    }
  }, [isReady]);

  // Timeline click handler
  const handleTimelineClick = useCallback((e) => {
    if (isDragging) return;
    
    const newTime = getTimelinePosition(e.clientX);
    seekToTime(newTime);
  }, [isDragging, getTimelinePosition, seekToTime]);

  // Unified pointer event handlers (works for both mouse and touch)
  const handlePointerDown = useCallback((e, type) => {
    if (!isMountedRef.current) return;
    
    // Prevent default only for our custom interaction
    e.stopPropagation();
    
    setIsDragging(true);
    setDragType(type);
    
    const startX = e.clientX || (e.touches && e.touches[0].clientX);
    const startTrimStart = trimStart;
    const startTrimEnd = trimEnd;
    
    const handlePointerMove = (moveEvent) => {
      if (!isMountedRef.current) return;
      
      const clientX = moveEvent.clientX || (moveEvent.touches && moveEvent.touches[0].clientX);
      const deltaX = clientX - startX;
      const rect = timelineRef.current?.getBoundingClientRect();
      if (!rect) return;
      
      const deltaTime = (deltaX / rect.width) * duration;
      
      if (type === 'start') {
        const newStart = Math.max(0, Math.min(startTrimStart + deltaTime, trimEnd - 1));
        setTrimStart(newStart);
        seekToTime(newStart);
      } else if (type === 'end') {
        const newEnd = Math.min(duration, Math.max(startTrimEnd + deltaTime, trimStart + 1));
        const maxEnd = trimStart + maxDuration;
        setTrimEnd(Math.min(newEnd, maxEnd));
      } else if (type === 'middle') {
        const trimLength = startTrimEnd - startTrimStart;
        const newStart = Math.max(0, Math.min(startTrimStart + deltaTime, duration - trimLength));
        const newEnd = newStart + trimLength;
        
        if (newEnd <= duration && newStart >= 0) {
          setTrimStart(newStart);
          setTrimEnd(newEnd);
          seekToTime(newStart);
        }
      }
    };
    
    const handlePointerUp = () => {
      if (!isMountedRef.current) return;
      
      setIsDragging(false);
      setDragType(null);
      document.removeEventListener('mousemove', handlePointerMove);
      document.removeEventListener('mouseup', handlePointerUp);
      document.removeEventListener('touchmove', handlePointerMove);
      document.removeEventListener('touchend', handlePointerUp);
    };
    
    // Add both mouse and touch listeners
    document.addEventListener('mousemove', handlePointerMove);
    document.addEventListener('mouseup', handlePointerUp);
    document.addEventListener('touchmove', handlePointerMove, { passive: false });
    document.addEventListener('touchend', handlePointerUp);
  }, [trimStart, trimEnd, duration, maxDuration, seekToTime]);

  // Slider-based trim controls as fallback
  const handleTrimStartSlider = useCallback((e) => {
    const value = parseFloat(e.target.value);
    const newStart = Math.max(0, Math.min(value, trimEnd - 1));
    setTrimStart(newStart);
    seekToTime(newStart);
  }, [trimEnd, seekToTime]);

  const handleTrimEndSlider = useCallback((e) => {
    const value = parseFloat(e.target.value);
    const newEnd = Math.min(duration, Math.max(value, trimStart + 1));
    const maxEnd = trimStart + maxDuration;
    setTrimEnd(Math.min(newEnd, maxEnd));
  }, [duration, trimStart, maxDuration]);

  // Create trimmed file
  const createTrimmedFile = useCallback(async () => {
    try {
      const trimmedFile = new File([file], file.name, {
        type: file.type,
        lastModified: file.lastModified
      });
      
      trimmedFile.trimStart = trimStart;
      trimmedFile.trimEnd = trimEnd;
      trimmedFile.originalDuration = duration;
      trimmedFile.isTrimmed = true;
      
      return trimmedFile;
    } catch (error) {
      console.error('Error creating trimmed file:', error);
      throw error;
    }
  }, [file, trimStart, trimEnd, duration]);

  // Handle save
  const handleSave = useCallback(async () => {
    setIsProcessing(true);
    try {
      const trimmedFile = await createTrimmedFile();
      await onSave(trimStart, trimEnd, trimmedFile);
      onClose();
    } catch (error) {
      console.error('Error saving trimmed video:', error);
      alert('Error processing video. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [createTrimmedFile, onSave, trimStart, trimEnd, onClose]);

  // Handle close
  const handleClose = useCallback(() => {
    isMountedRef.current = false;
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
    cleanup();
    onClose();
  }, [cleanup, onClose]);

  if (!isOpen) return null;

  const selectedDuration = trimEnd - trimStart;
  const isValidSelection = selectedDuration <= maxDuration && selectedDuration >= 1;
  
  // Calculate positions for timeline
  const startPercent = duration > 0 ? (trimStart / duration) * 100 : 0;
  const endPercent = duration > 0 ? (trimEnd / duration) * 100 : 0;
  const currentPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className={styles['modal-overlay']}>
      <div className={styles['modal-content']}>
        <button className={styles['modal-close']} onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        
        <h2>
          <FontAwesomeIcon icon={faScissors} /> Trim Your Video
        </h2>
        
        <div className={styles['trim-info']}>
          <p>Your video is {formatTime(duration)} long, but your membership allows up to {formatTime(maxDuration)}.</p>
          <p>Drag the handles on the timeline or use the sliders below to select which part to keep.</p>
        </div>
        
        {/* Video Preview */}
        <div className={styles['video-container']}>
          <video
            ref={videoRef}
            className={styles['preview-video']}
            playsInline
            preload="metadata"
          />
          
          <div className={styles['video-overlay']}>
            <button 
              onClick={togglePlayPause} 
              className={styles['play-button']}
              disabled={!isReady}
            >
              <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
            </button>
            
            <button 
              onClick={toggleMute}
              className={styles['mute-button']}
            >
              <FontAwesomeIcon icon={isMuted ? faVolumeMute : faVolumeUp} />
            </button>
          </div>
          
          {!isReady && (
            <div className={styles['loading-video']}>
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>Loading video...</span>
            </div>
          )}
        </div>

        {isReady && (
          <>
            {/* Timeline */}
            <div className={styles['timeline-container']}>
              <div 
                ref={timelineRef}
                className={styles['timeline']}
                onClick={handleTimelineClick}
              >
                {/* Thumbnail Track */}
                <div className={styles['thumbnail-track']}>
                  {thumbnails.map((thumb, index) => (
                    <div
                      key={index}
                      className={styles['thumbnail']}
                      style={{
                        backgroundImage: `url(${thumb.dataUrl})`,
                        left: `${(thumb.time / duration) * 100}%`,
                        width: `${100 / thumbnails.length}%`
                      }}
                    />
                  ))}
                </div>

                {/* Trim Selection */}
                <div 
                  className={styles['trim-selection']}
                  style={{
                    left: `${startPercent}%`,
                    width: `${endPercent - startPercent}%`
                  }}
                >
                  {/* Start Handle */}
                  <div
                    className={`${styles['trim-handle']} ${styles['start-handle']}`}
                    onMouseDown={(e) => handlePointerDown(e, 'start')}
                    onTouchStart={(e) => handlePointerDown(e, 'start')}
                  />
                  
                  {/* Middle Drag Area */}
                  <div
                    className={styles['trim-middle']}
                    onMouseDown={(e) => handlePointerDown(e, 'middle')}
                    onTouchStart={(e) => handlePointerDown(e, 'middle')}
                  />
                  
                  {/* End Handle */}
                  <div
                    className={`${styles['trim-handle']} ${styles['end-handle']}`}
                    onMouseDown={(e) => handlePointerDown(e, 'end')}
                    onTouchStart={(e) => handlePointerDown(e, 'end')}
                  />
                </div>

                {/* Current Time Indicator */}
                <div 
                  className={styles['current-time-indicator']}
                  style={{ left: `${currentPercent}%` }}
                />
                
                {/* Dimmed Areas */}
                <div 
                  className={styles['dimmed-area']}
                  style={{ width: `${startPercent}%` }}
                />
                <div 
                  className={styles['dimmed-area']}
                  style={{ 
                    left: `${endPercent}%`,
                    width: `${100 - endPercent}%`
                  }}
                />
              </div>
            </div>

            {/* Slider Controls as Fallback */}
            <div className={styles['slider-controls']}>
              <div className={styles['slider-group']}>
                <label>Start: {formatTime(trimStart)}</label>
                <input
                  type="range"
                  min="0"
                  max={duration}
                  step="0.1"
                  value={trimStart}
                  onChange={handleTrimStartSlider}
                  className={styles['trim-slider']}
                />
              </div>
              
              <div className={styles['slider-group']}>
                <label>End: {formatTime(trimEnd)}</label>
                <input
                  type="range"
                  min="0"
                  max={duration}
                  step="0.1"
                  value={trimEnd}
                  onChange={handleTrimEndSlider}
                  className={styles['trim-slider']}
                />
              </div>
            </div>

            {/* Controls */}
            <div className={styles['controls']}>
              <div className={styles['time-display']}>
                <span>Selection: {formatTime(trimStart)} - {formatTime(trimEnd)}</span>
                <span className={selectedDuration > maxDuration ? styles['invalid'] : styles['valid']}>
                  Duration: {formatTime(selectedDuration)} / {formatTime(maxDuration)}
                </span>
              </div>
              
              {selectedDuration > maxDuration && (
                <div className={styles['warning']}>
                  Selection exceeds maximum duration. Please adjust the selection.
                </div>
              )}
              
              <div className={styles['control-buttons']}>
                <button 
                  onClick={playTrimmedSection}
                  className={styles['preview-button']}
                  disabled={!isReady}
                >
                  <FontAwesomeIcon icon={faPlay} />
                  Preview Selection
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={styles['form-actions']}>
              <button 
                type="button" 
                className={styles['secondary-button']}
                onClick={() => navigate('/membership')}
              >
                <FontAwesomeIcon icon={faArrowUp} /> Upgrade Membership
              </button>
              <button 
                type="button" 
                className={styles['primary-button']}
                onClick={handleSave}
                disabled={!isValidSelection || isProcessing}
              >
                {isProcessing ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Processing...
                  </>
                ) : (
                  'Apply Trim'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VideoTrimmerModal;